import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import HeadData from 'components/Common/HeadData'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'
import ExperienceSearchPageFilters from './ExperienceSearchPageFilters'
import GeoContext from 'contexts/geoContext'
import ExperienceSearchPageList from './ExperienceSearchPageList'
import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import Places, { getPlaceByRegionCode } from 'constants/places'
import { fetchExperienceCategories, fetchExperienceListFacets } from 'actions/ExperienceActions'
import useQueryParams from 'hooks/useQueryParams'
import { useAppDispatch } from 'hooks/reduxHooks'
import {
  usePlaceAncestorsById,
  usePlaceByCoords,
  usePlaceById,
} from 'hooks/usePlace'
import { EmptyArray, without } from 'lib/array/arrayUtils'
import { getAttractionOffersKey, hasActiveExperienceSideFilter, mapTravelStylesToTargetAudience } from 'lib/experiences/experienceUtils'
import { clearAttractionAttribution } from 'lib/attractions/attractionAttribution'
import { clearContentTagAttribution } from 'lib/contentTags/contentTagAttribution'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import ExperienceSearchPageContainer from './ExperienceSearchPageContainer'
import useExperience from 'hooks/Experiences/useExperience'
import Error404Page from 'components/Pages/Error404Page'
import RedirectWithStatus from 'components/Common/RedirectWithStatus/RedirectWithStatus'
import { listHasPlaceId } from './utils'
import MapWindow from 'components/Search/MapWindow'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import { getExperiencesSearchHeadMeta } from './getExperiencesSearchHeadMeta'
import ExperienceSearchTopAttractions from './ExperienceSearchTopAttractions'
import ExperienceSearchRecentFilters from './ExperienceSearchRecentFilters'
import ExperienceSearchAttractionResults from './ExperienceSearchAttractionResults'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import useExperiment from 'hooks/Experiments/useExperiment'
import { ChertoExperiments } from 'constants/experiments'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'
import ExperienceSearchTile from '../ExperienceTiles/ExperienceSearchTile'
import useAddExperienceRecentSearch from 'components/Experiences/hooks/useAddExperienceRecentSearch'
import useTopAttractions from 'hooks/Experiences/useTopAttractions'
import SiteTakeoverSearchBanner from 'takeovers/utils/components/SiteTakeoverSearchBanner'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'
import { TRANSFER_SEARCH_CATEGORY_IDS, TRANSFER_SEARCH_VERTICAL } from 'constants/experience'
import useContentTagOffers from 'hooks/Experiences/useContentTagOffers'
import ExperienceSearchContentTagResults from './ExperienceSearchContentTagResults'

const PageContent = styled.div`
  display: grid;
  margin-top: ${rem(-4)};
  grid-template-columns: minmax(0, 1fr);

  ${mediaQueryUp.desktop} {
    grid-template-columns: ${rem(308)} minmax(0, 1fr);
    column-gap: 0;
  }

`

const DesktopFilterBreakpoint = styled(CSSBreakpoint)`
  width: ${rem(280)};
  position: sticky;
  top: ${rem(57)};
  max-height: calc(100vh - ${rem(57)});
  overflow-y: auto;
  padding-top: ${rem(20)};

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  > * + * {
    margin-top: ${rem(12)};
  }
`

const HighlightOfferWrapper = styled(VerticalSpacer)`
  padding-top: ${rem(24)};
`

interface OwnProps {
  skipHeadData?: boolean;
}

interface MappedStateProps {
  search: string;
}

type Props = OwnProps & MappedStateProps

export function ExperienceSearchPageContent(props: Props) {
  const { search, skipHeadData } = props

  const intl = useIntl()
  const geo = useContext(GeoContext)
  const queryParams = useQueryParams()
  const isTransfersSearch = queryParams.get('vertical') === TRANSFER_SEARCH_VERTICAL
  const dispatch = useAppDispatch()
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()
  const trackEvent = useTrackExperienceOfferEvent()
  const attractionsEnabled = useFeatureFlag(OptimizelyFeatureFlags.experiencesSearchAttractions)
  const { isAnyVariant: contentTagsEnabled } = useExperiment(ChertoExperiments.experiencesContentTagSearch)
  const shouldShowBanner = useShouldShowBanner({ location: 'search', vertical: 'experience' })
  const [landmarksVisible, setLandmarksVisible] = useState(false)
  const [selectedAttractionId, setSelectedAttractionId] = useState<
    string | undefined
  >()
  const [selectedAttractionOfferIds, setSelectedAttractionOfferIds] = useState<Array<string>>([])
  // Key for the offer IDs currently stored in state.
  const [selectedAttractionOfferIdsKey, setSelectedAttractionOfferIdsKey] = useState<string | undefined>()
  const [selectedAttractionOffersResolved, setSelectedAttractionOffersResolved] = useState(false)
  const [topAttractionsRendered, setTopAttractionsRendered] = useState(false)

  const experienceId = queryParams.get('experienceId') ?? undefined
  const [highlightedOffer, fetchingHighlightedOffer, highlightedOfferError] = useExperience(experienceId)
  const [highlightedOfferPlace, fetchingHighlightedOfferPlace] = usePlaceByCoords(
    highlightedOffer?.location?.latitude,
    highlightedOffer?.location?.longitude,
  )

  const placeId =
    (experienceId
      ? highlightedOfferPlace?.id
      : queryParams.get('destinationId')) ?? undefined

  // Key for the selected attraction under the current search filters.
  const selectedAttractionOffersKey = useMemo(
    () => selectedAttractionId ? getAttractionOffersKey(selectedAttractionId, searchFilters) : undefined,
    [selectedAttractionId, searchFilters],
  )

  const handleAttractionSelect = useCallback(
    (attractionId: string | undefined) => {
      // Clearing the landmark selection (deselect, hide toggle, leave the surface) must also
      // drop the le_attr attribution cookie, otherwise a later normal-experience purchase is
      // still credited to the stale landmark for the 7-day attribution window.
      if (!attractionId) {
        clearAttractionAttribution()
      }
      setSelectedAttractionId(attractionId)
      setSelectedAttractionOfferIds([])
      setSelectedAttractionOfferIdsKey(undefined)
      setSelectedAttractionOffersResolved(!attractionId)
    },
    [],
  )

  const handleClearAttraction = useCallback(
    () => handleAttractionSelect(undefined),
    [handleAttractionSelect],
  )

  useEffect(() => {
    if (!attractionsEnabled && selectedAttractionId) {
      handleClearAttraction()
    }
  }, [attractionsEnabled, handleClearAttraction, selectedAttractionId])

  const handleToggleLandmarks = useCallback(() => {
    setLandmarksVisible((visible) => !visible)
  }, [])

  // Hiding the Landmarks surface clears any selected landmark so the offer list returns to normal.
  useEffect(() => {
    if (!landmarksVisible && selectedAttractionId) {
      handleClearAttraction()
    }
  }, [landmarksVisible, selectedAttractionId, handleClearAttraction])

  const handleAttractionOfferIdsStateChange = useCallback(
    ({ offerIds, resolved }: { offerIds: Array<string>; resolved: boolean }) => {
      setSelectedAttractionOfferIds(offerIds)
      setSelectedAttractionOfferIdsKey(selectedAttractionOffersKey)
      setSelectedAttractionOffersResolved(resolved)
    },
    // Store offer IDs against the key that produced them, not the key from the first selection.
    [selectedAttractionOffersKey],
  )

  const setSearchFiltersRef = useRef(setSearchFilters)
  setSearchFiltersRef.current = setSearchFilters

  const searchFiltersRef = useRef(searchFilters)
  searchFiltersRef.current = searchFilters

  const prevPlaceIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const previousPlaceId = prevPlaceIdRef.current
    prevPlaceIdRef.current = placeId
    if (previousPlaceId !== undefined && placeId !== undefined && previousPlaceId !== placeId) {
      setSearchFiltersRef.current({
        checkIn: searchFiltersRef.current.checkIn,
        checkOut: searchFiltersRef.current.checkOut,
        campaigns: searchFiltersRef.current.campaigns,
        luxPlusFeatures: searchFiltersRef.current.luxPlusFeatures,
      })
    }

    if (previousPlaceId !== undefined && previousPlaceId !== placeId) {
      // This resets the landmark state directly (not via handleAttractionSelect), so clear the
      // le_attr attribution cookie here too when switching destinations.
      clearAttractionAttribution()
      setLandmarksVisible(false)
      setSelectedAttractionId(undefined)
      setSelectedAttractionOfferIds([])
      setSelectedAttractionOfferIdsKey(undefined)
      setSelectedAttractionOffersResolved(false)
      setTopAttractionsRendered(false)
    }
  }, [isTransfersSearch, placeId])

  const placeName =
    (experienceId
      ? highlightedOfferPlace?.name
      : queryParams.get('destinationName')) ?? undefined
  const experienceName = queryParams.get('experienceName')

  const regionPlace = useMemo(
    () => getPlaceByRegionCode(geo.currentRegionCode),
    [geo.currentRegionCode],
  )

  const [ancestorPlaceIds, fetchingAncestors] = usePlaceAncestorsById(placeId)

  useEffect(() => {
    dispatch(fetchExperienceCategories())
  }, [dispatch])

  useEffect(() => {
    if (isTransfersSearch && placeId) {
      dispatch(fetchExperienceListFacets(placeId, {
        categoryValues: TRANSFER_SEARCH_CATEGORY_IDS.join(','),
      }))
    }
  }, [dispatch, isTransfersSearch, placeId])

  useAddExperienceRecentSearch(queryParams)

  const fallbackPlaceIds = useMemo(() => {
    if (searchFilters.collections?.length) {
      return EmptyArray
    }

    const ancestorIds = without(ancestorPlaceIds, Places.Anywhere.id)
    return [...new Set([...ancestorIds, regionPlace.id])]
  }, [ancestorPlaceIds, regionPlace, searchFilters.collections])

  const targetAudience = useMemo(
    () =>
      searchFilters.travelStyles?.length
        ? mapTravelStylesToTargetAudience(searchFilters.travelStyles)
        : undefined,
    [searchFilters.travelStyles],
  )
  const hasFreeCancellation =
    searchFilters.featured?.includes('free-cancellation') || undefined
  const hasInstantConfirmation =
    searchFilters.featured?.includes('instant-confirmation') || undefined
  const isGift = searchFilters.featured?.includes('is-gift') || undefined

  const list = useExperienceSearchList({
    placeId,
    fallbackPlaceIds: fetchingAncestors ? EmptyArray : fallbackPlaceIds,
    categoryCodes: isTransfersSearch ? [...TRANSFER_SEARCH_CATEGORY_IDS] : searchFilters.categoryCodes,
    priceLte: searchFilters.priceLte,
    priceGte: searchFilters.priceGte,
    campaigns: searchFilters.campaigns,
    luxPlusFeatures: searchFilters.luxPlusFeatures,
    collections: searchFilters.collections,
    sortBy: 'type',
    minRating: searchFilters.minRating,
    leExclusive: searchFilters.leExclusive,
    targetAudience,
    hasFreeCancellation,
    hasInstantConfirmation,
    isGift,
    from: searchFilters.checkIn,
    to: searchFilters.checkOut,
    timeSlots: searchFilters.timeSlots,
  })

  const selectedContentTagId = contentTagsEnabled ? searchFilters.contentTagId : undefined
  const { offerIds: contentTagOfferIds, fetching: fetchingContentTagOffers, error: contentTagOfferError } = useContentTagOffers(placeId, selectedContentTagId)
  const visibleContentTagOfferIds = useMemo(() => {
    if (!selectedContentTagId) return EmptyArray
    if (!hasActiveExperienceSideFilter(searchFilters)) return contentTagOfferIds
    const tagOfferIds = new Set(contentTagOfferIds)
    return list.ids.filter(id => tagOfferIds.has(id))
  }, [contentTagOfferIds, list.ids, searchFilters, selectedContentTagId])
  const clearContentTag = useCallback(() => {
    clearContentTagAttribution()
    setSearchFilters({ ...searchFilters, contentTagId: undefined })
  }, [searchFilters, setSearchFilters])

  const handleViewMapClick = useCallback(() => {
    trackEvent('VIEW_ON_MAP_CLICKED')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  const isHighlightedOfferSoldOut =
    highlightedOffer && highlightedOffer?.status !== 'ONLINE'
  const showHighlightedTile = highlightedOffer && !isHighlightedOfferSoldOut

  // hide the list when you only found the highlighted one
  const showList = !(
    list.ids.length === 1 && list.ids[0] === highlightedOffer?.id
  )

  const finalPlace = usePlaceById(list.placeId)
  const inputPlace = usePlaceById(placeId)
  const attractionPlace = inputPlace ?? finalPlace
  const topAttractionsState = useTopAttractions(attractionsEnabled ? attractionPlace : undefined)
  const isAnywhereSearch = list.placeId === Places.Anywhere.id
  const metaPlaceName = !isAnywhereSearch
    ? finalPlace?.name ?? placeName ?? undefined
    : undefined

  const searchHeadMeta = useMemo(
    () =>
      getExperiencesSearchHeadMeta({
        placeName: metaPlaceName,
        isAnywhere: isAnywhereSearch,
        intl,
      }),
    [metaPlaceName, isAnywhereSearch, intl],
  )

  const searchMode = useMemo(
    () => {
      const searchQuery = search.startsWith('?') ? search.slice(1) : search
      if (isTransfersSearch) {
        return {
          url: `/search/transfers/map${searchQuery ? `?${searchQuery}` : ''}`,
          mode: 'list',
        }
      }

      return {
        url: `/search/experiences/map${searchQuery ? `?${searchQuery}` : ''}`,
        mode: 'list',
      }
    },
    [isTransfersSearch, search],
  )

  const destinationName = placeName ?? finalPlace?.name ?? ''
  const destinationLocationPhrase = destinationName ? formatPlacePhrase({ location: { name: destinationName }, language: intl.locale, intent: 'location' }) : ''
  const highlightedOfferId = highlightedOffer?.id
  const hasCurrentSelectedAttractionOfferIds = Boolean(
    selectedAttractionId &&
    selectedAttractionOfferIdsKey === selectedAttractionOffersKey,
  )
  const suppressSelectedAttractionResults = Boolean(
    selectedAttractionId &&
    placeId &&
    list.isFallbackPlace,
  )

  const normalListExcludeIds = useMemo(() => {
    const ids = [
      ...(highlightedOfferId ? [highlightedOfferId] : []),
      ...(hasCurrentSelectedAttractionOfferIds ? selectedAttractionOfferIds : []),
    ]

    return ids.length > 0 ? [...new Set(ids)] : undefined
  }, [highlightedOfferId, hasCurrentSelectedAttractionOfferIds, selectedAttractionOfferIds])

  const shouldShowNormalList =
    showList && !selectedContentTagId && (!selectedAttractionId || (selectedAttractionOffersResolved && hasCurrentSelectedAttractionOfferIds))
  const shouldShowMoreExperiencesHeading = Boolean(
    selectedAttractionId &&
    list.ids.some(id => !normalListExcludeIds?.includes(id)),
  )

  const resolvingHighlightedOfferPlace = Boolean(
    experienceId &&
    !placeId &&
    !highlightedOfferError &&
    (!highlightedOffer || fetchingHighlightedOffer || fetchingHighlightedOfferPlace),
  )

  const shouldShowLandmarksToggle = Boolean(
    attractionsEnabled &&
    attractionPlace &&
    topAttractionsState.isCuratedDestination !== false &&
    (topAttractionsState.fetching || topAttractionsState.attractions.length > 0),
  )

  useEffect(() => {
    if (!shouldShowLandmarksToggle && landmarksVisible) {
      setLandmarksVisible(false)
    }
  }, [shouldShowLandmarksToggle, landmarksVisible])

  const landmarksToggle = useMemo(
    () => ({
      show: shouldShowLandmarksToggle,
      active: landmarksVisible,
      onToggle: handleToggleLandmarks,
    }),
    [shouldShowLandmarksToggle, landmarksVisible, handleToggleLandmarks],
  )

  if (resolvingHighlightedOfferPlace || (list.fetching && !selectedContentTagId)) {
    return <LoadingIndicator />
  }

  if (!listHasPlaceId(list)) {
    if (!queryParams.get('destinationId')?.trim()) {
      return (
        <RedirectWithStatus
          to={`/${geo.currentRegionCode.toLowerCase()}/experiences`}
          code={302}
          withSearchAndHash={false}
        />
      )
    }
    return <Error404Page />
  }

  const noResultsForExperienceHeading = intl.formatMessage(
    {
      id: 'VsybqlRA14lcfgh9veqZ',
      defaultMessage: 'Sorry, we couldn’t find any experiences for {name}',
      description: 'Heading when a highlighted experience offer is sold out',
    },
    { name: experienceName },
  )
  const tryDifferentLocationText =
    list.placeId !== Places.Anywhere.id
      ? intl.formatMessage(
          {
            id: 'z_5zerbj08o7t8F1yICy',
            defaultMessage:
              'Try a different location or explore our top offers in {placeName}',
            description:
              'Suggestion text with place name when no experience results found',
          },
          { placeName: finalPlace ? formatPlacePhrase({ location: { name: finalPlace.name }, language: intl.locale, intent: 'location' }) : undefined },
        )
      : intl.formatMessage({
          id: 'ohea11GbXfiNCR73RIQ8',
          defaultMessage: 'Try a different location or explore our top offers',
          description:
            'Suggestion text when no experience results found in the experiences section',
        })

  return (
    <>
      {shouldShowBanner && <SiteTakeoverSearchBanner vertical="experience" />}
      <ExperienceSearchPageContainer
        searchMode={searchMode}
        list={list}
        place={finalPlace}
        landmarksToggle={landmarksToggle}
        isTransfersSearch={isTransfersSearch}
      >
        <>
          {!skipHeadData && (
            <HeadData
              noIndex
              title={searchHeadMeta.title}
              description={searchHeadMeta.description}
              imageId={searchHeadMeta.imageId}
              imageUrl={searchHeadMeta.imageUrl}
              imageAlt={searchHeadMeta.imageAlt}
            />
          )}
          <Pane>
            <LayoutContainer variant="extraLarge">
              <PageContent>
                <DesktopFilterBreakpoint min="desktop">
                  <VerticalSpacer gap={16}>
                    {!isTransfersSearch && <ExperienceSearchRecentFilters />}
                    {!selectedContentTagId && (
                      <MapWindow
                        mapViewURL={searchMode.url}
                        onClick={handleViewMapClick}
                      />
                    )}
                    <ExperienceSearchPageFilters
                      place={finalPlace}
                      listIds={list.ids}
                    />
                  </VerticalSpacer>
                </DesktopFilterBreakpoint>
                <VerticalSpacer gap={16}>
                  {showHighlightedTile && (
                    <HighlightOfferWrapper gap={4}>
                      <ExperienceSearchTile experience={highlightedOffer} />
                    </HighlightOfferWrapper>
                  )}
                  {/* The toggle chip is rendered in the filter row (header); here only the carousel. */}
                  {shouldShowLandmarksToggle && landmarksVisible && (
                    <ExperienceSearchTopAttractions
                      place={attractionPlace}
                      selectedAttractionId={selectedAttractionId}
                      onAttractionSelect={handleAttractionSelect}
                      onRenderStateChange={setTopAttractionsRendered}
                    />
                  )}
                  {isHighlightedOfferSoldOut && (
                    <div>
                      <Heading variant="heading4">
                        {noResultsForExperienceHeading}
                      </Heading>
                      <BodyText variant="large">
                        {tryDifferentLocationText}
                      </BodyText>
                    </div>
                  )}
                  {shouldShowLandmarksToggle && landmarksVisible && selectedAttractionId && (
                    <ExperienceSearchAttractionResults
                      attractionId={selectedAttractionId}
                      destination={attractionPlace?.name}
                      searchFilters={searchFilters}
                      suppressResults={suppressSelectedAttractionResults}
                      onOfferIdsStateChange={handleAttractionOfferIdsStateChange}
                      onClearAttraction={handleClearAttraction}
                    />
                  )}
                  {selectedContentTagId && !list.isFallbackPlace && (
                    <ExperienceSearchContentTagResults
                      offerIds={visibleContentTagOfferIds}
                      fetching={fetchingContentTagOffers}
                      listFetching={list.fetching && hasActiveExperienceSideFilter(searchFilters)}
                      error={contentTagOfferError}
                      onClear={clearContentTag}
                      contentTagId={selectedContentTagId}
                    />
                  )}
                  {selectedContentTagId && list.isFallbackPlace && (
                    <ExperienceSearchContentTagResults
                      offerIds={[]}
                      fetching={false}
                      listFetching={list.fetching}
                      onClear={clearContentTag}
                      contentTagId={selectedContentTagId}
                    />
                  )}
                  {shouldShowNormalList && (
                    <VerticalSpacer gap={12}>
                      {shouldShowMoreExperiencesHeading && (
                        <Heading variant="heading4">
                          {intl.formatMessage(
                            {
                              id: 'KFwRybZ6aPvAYBYDx_ve',
                              defaultMessage: 'More experiences in {destinationName}',
                              description: 'Heading above normal destination results shown below selected attraction results',
                            },
                            { destinationName: destinationLocationPhrase },
                          )}
                        </Heading>
                      )}
                      <ExperienceSearchPageList
                        list={list}
                        destinationName={destinationName}
                        excludeIds={normalListExcludeIds}
                        showNoResultsSummary={!selectedAttractionId}
                        compactTopSpacing={topAttractionsRendered && !selectedAttractionId}
                      />
                    </VerticalSpacer>
                  )}
                </VerticalSpacer>
              </PageContent>
            </LayoutContainer>
          </Pane>
        </>
      </ExperienceSearchPageContainer>
    </>
  )
}

function mapStateToProps(
  state: App.State,
  _ownProps: OwnProps,
): MappedStateProps {
  return {
    search: state.router.location.search,
  }
}

export default connect<MappedStateProps, undefined, OwnProps, App.State>(
  mapStateToProps,
)(ExperienceSearchPageContent)
